"use client";

import { useEffect } from "react";
import type { AwsRum } from "aws-rum-web";
import { useUser } from "@/lib/auth/use-user";
import {
  initKeystrokeLagTracker,
  updateKeystrokeLagBaseAttrs,
} from "@/utils/keystroke-lag-tracker";
import {
  captureUserContext,
  updateSessionContext,
  flattenUserContext,
  fetchIpLocation,
  updateContextWithIpLocation,
  type UserContextData,
} from "@/utils/rum-user-context";

let awsRum: AwsRum | null = null;
let globalUserContext: UserContextData | null = null;
let ipLocationFetched = false;
let globalIpLocation: {
  ipAddress?: string;
  city?: string;
  region?: string;
  regionCode?: string;
  country?: string;
  countryCode?: string;
  continent?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  currency?: string;
  isp?: string;
  asn?: string;
} | null = null;

/**
 * Initialize AWS CloudWatch RUM for frontend monitoring
 * Tracks errors, performance metrics, and HTTP calls automatically
 */
export function initializeRum() {
  // Only run on client-side
  if (typeof window === "undefined") return null;

  // Skip RUM in local development — vars won't be set and RUM isn't needed locally
  if (process.env.NODE_ENV === "development") {
    return null;
  }

  // Check SST_STAGE to determine if RUM should be enabled
  // Enable RUM for production, staging, OR when SST_STAGE is not set
  // Disable ONLY for explicit "develop" stage
  const sstStage = process.env.NEXT_PUBLIC_SST_STAGE || process.env.SST_STAGE;

  if (sstStage === "develop") {
    console.log(`⏭️  Skipping CloudWatch RUM (SST_STAGE: develop)`);
    return null;
  }

  // Return existing instance if already initialized
  if (awsRum) return awsRum;

  console.log(`🚀 Initializing CloudWatch RUM (SST_STAGE: ${sstStage || "not set - defaulting to enabled"})`);

  // Capture comprehensive user context
  globalUserContext = captureUserContext();
  console.log("📊 Captured user context:", {
    browser: globalUserContext.browser?.name,
    os: globalUserContext.os?.name,
    device: globalUserContext.device?.type,
    timezone: globalUserContext.locale?.timezone,
    screen: `${globalUserContext.screen?.width}x${globalUserContext.screen?.height}`,
  });

  // Check for required environment variables
  const identityPoolId = process.env.NEXT_PUBLIC_RUM_IDENTITY_POOL_ID;
  const guestRoleArn = process.env.NEXT_PUBLIC_RUM_GUEST_ROLE_ARN;
  // NOTE: The App Monitor ID is the UUID (e.g., d043a5b6-8bd2-40a5-a290-629f019b596c), NOT the name
  // You can find it in AWS Console > CloudWatch > RUM > Application monitors > [your app] > App monitor ID
  const appMonitorId = process.env.NEXT_PUBLIC_RUM_APP_MONITOR_ID;
  const region = process.env.NEXT_PUBLIC_RUM_REGION || "us-east-1";

  if (!identityPoolId || !guestRoleArn || !appMonitorId) {
    console.warn("⚠️  CloudWatch RUM not configured: Missing required environment variables");
    console.warn("  Required: NEXT_PUBLIC_RUM_IDENTITY_POOL_ID, NEXT_PUBLIC_RUM_GUEST_ROLE_ARN, NEXT_PUBLIC_RUM_APP_MONITOR_ID");
    return null;
  }

  try {
    // Dynamic import to avoid SSR issues
    const { AwsRum } = require("aws-rum-web");

    const config = {
      sessionSampleRate: 1, // 100% of sessions - adjust to 0.1 for 10% if needed
      identityPoolId,
      endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
      telemetries: ["errors", "performance", "http"],
      allowCookies: true,
      enableXRay: true,
      guestRoleArn,
      // Event batching configuration
      eventPluginsToLoad: [],
      recordResourceUrl: true,
      dispatchInterval: 5000, // Send every 5 seconds
      batchLimit: 10, // Send after 10 events
    };

    awsRum = new AwsRum(
      appMonitorId,
      "1.0.0",
      region,
      config,
    );

    console.log("📊 RUM instance created, enabling data collection...");

    // NOTE: AWS RUM limits custom session attributes to 10 per event
    // IP location attributes are added asynchronously when available

    // Fetch IP location asynchronously (non-blocking)
    if (!ipLocationFetched) {
      ipLocationFetched = true;
      fetchIpLocation()
        .then((ipLocation) => {
          if (ipLocation && awsRum) {
            updateContextWithIpLocation(ipLocation);

            // Store globally for use in custom events
            globalIpLocation = {
              ipAddress: ipLocation.ipAddress || undefined,
              city: ipLocation.city || undefined,
              region: ipLocation.region || undefined,
              regionCode: ipLocation.regionCode || undefined,
              country: ipLocation.country || undefined,
              countryCode: ipLocation.countryCode || undefined,
              continent: ipLocation.continent || undefined,
              postalCode: ipLocation.postalCode || undefined,
              latitude: ipLocation.latitude || undefined,
              longitude: ipLocation.longitude || undefined,
              timezone: ipLocation.timezone || undefined,
              currency: ipLocation.currency || undefined,
              isp: ipLocation.isp || undefined,
              asn: ipLocation.asn || undefined,
            };

            // Add IP location to session attributes (appears in metadata for all events)
            // Using 5 of the 10 custom attribute limit
            awsRum.addSessionAttributes({
              ipAddress: ipLocation.ipAddress || "unknown",
              ipCity: ipLocation.city || "unknown",
              ipRegion: ipLocation.region || "unknown",
              ipCountry: ipLocation.country || "unknown",
              ipIsp: ipLocation.isp || "unknown",
            });

            // Record a dedicated user_location event with all IP details
            awsRum.recordEvent("user_location", {
              ipAddress: ipLocation.ipAddress,
              city: ipLocation.city,
              region: ipLocation.region,
              regionCode: ipLocation.regionCode,
              country: ipLocation.country,
              countryCode: ipLocation.countryCode,
              continent: ipLocation.continent,
              postalCode: ipLocation.postalCode,
              latitude: ipLocation.latitude,
              longitude: ipLocation.longitude,
              timezone: ipLocation.timezone,
              currency: ipLocation.currency,
              isp: ipLocation.isp,
              asn: ipLocation.asn,
              timestamp: new Date().toISOString(),
            });

            console.log("📍 IP Location recorded:", {
              ip: ipLocation.ipAddress,
              city: ipLocation.city,
              region: ipLocation.region,
              country: ipLocation.country,
              isp: ipLocation.isp,
            });
          }
        })
        .catch((error) => {
          console.warn("Failed to fetch IP location:", error);
        });
    }

    console.log("✅ CloudWatch RUM initialized successfully");
    return awsRum;
  } catch (error) {
    console.error("Failed to initialize CloudWatch RUM:", error);
    return null;
  }
}

/**
 * Get the RUM instance for manual event tracking
 */
export function getRumInstance() {
  return awsRum;
}

/**
 * Get the captured user context
 */
export function getUserContext() {
  return globalUserContext;
}

/**
 * Get the captured IP location data
 */
export function getIpLocation() {
  return globalIpLocation;
}

/**
 * Update the current page in session context
 */
export function updatePageContext() {
  updateSessionContext();
  if (awsRum && typeof window !== "undefined") {
    awsRum.addSessionAttributes({
      currentPage: window.location.pathname,
    });
  }
}

/**
 * Record a custom event with automatic user context enrichment
 * Includes IP location data if available
 */
export function recordRumEvent(eventType: string, eventData: any) {
  if (!awsRum) {
    if (process.env.NODE_ENV !== "development") {
      console.warn(`⚠️ RUM not initialized - event not recorded: ${eventType}`);
    }
    return;
  }

  try {
    // Merge event data with context info and IP location
    const enrichedData = {
      ...eventData,
      // Add current page and online status to every event
      _currentPage: typeof window !== "undefined" ? window.location.pathname : undefined,
      _onLine: typeof navigator !== "undefined" ? navigator.onLine : undefined,
      _timestamp: new Date().toISOString(),
      // Include IP location if available
      ...(globalIpLocation && {
        _ipAddress: globalIpLocation.ipAddress,
        _ipCity: globalIpLocation.city,
        _ipRegion: globalIpLocation.region,
        _ipCountry: globalIpLocation.country,
        _ipCountryCode: globalIpLocation.countryCode,
        _ipIsp: globalIpLocation.isp,
        _ipLatitude: globalIpLocation.latitude,
        _ipLongitude: globalIpLocation.longitude,
      }),
    };
    awsRum.recordEvent(eventType, enrichedData);
    console.debug(`✅ RUM event recorded: ${eventType}`, enrichedData);
  } catch (error) {
    console.error(`❌ Failed to record RUM event: ${eventType}`, error);
  }
}

/**
 * Record the full user context as a single event
 * Useful for capturing a complete snapshot at a specific point
 */
export function recordFullUserContext() {
  if (!awsRum || !globalUserContext) {
    if (process.env.NODE_ENV !== "development") {
      console.warn("⚠️ RUM not initialized - user context not recorded");
    }
    return;
  }

  try {
    const flatContext = flattenUserContext(globalUserContext);
    awsRum.recordEvent("user_context_full", flatContext);
    console.debug("✅ Full user context recorded");
  } catch (error) {
    console.error("❌ Failed to record full user context:", error);
  }
}

/**
 * Record an error
 */
export function recordRumError(error: Error, additionalData?: any) {
  if (awsRum) {
    try {
      awsRum.recordError(error);
      // If there's additional data, record it as a separate event
      if (additionalData) {
        awsRum.recordEvent("errorContext", additionalData);
      }
    } catch (err) {
      console.error("Failed to record RUM error:", err);
    }
  }
}

/**
 * Provider component that initializes RUM on mount
 */
export default function RumProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user, isLoading } = useUser();

  useEffect(() => {
    initializeRum();
    // Start the keystroke-lag tracker once per page load. It listens for
    // PerformanceEventTiming entries from the Notes editor and emits the
    // `note.keystroke_lag` RUM event. Init order does not matter — if RUM
    // is not yet up, the tracker's first samples will warn and drop, but
    // subsequent samples land normally as soon as the AwsRum instance is
    // ready. See utils/keystroke-lag-tracker.ts for the rationale.
    initKeystrokeLagTracker();
  }, []);

  // Add user/session data to RUM when user loads (non-blocking)
  useEffect(() => {
    if (!isLoading && awsRum && user) {
      try {
        // Cast user to access custom properties
        const userWithMetadata = user as any;

        // Add user attributes (excluding sensitive tokens)
        const attributes: Record<string, string | number | boolean> = {
          // User identification
          userId: user.sub || "anonymous",
        };

        // Add optional fields only if they exist
        if (user.email_verified !== undefined) {
          attributes.emailVerified = user.email_verified;
        }
        if (user.updated_at) {
          attributes.updatedAt = user.updated_at;
        }

        // Organization context
        if (userWithMetadata.saxId || userWithMetadata.sax_id) {
          attributes.saxId = userWithMetadata.saxId || userWithMetadata.sax_id;
        }
        if (userWithMetadata.practiceId || userWithMetadata.practice_id) {
          attributes.practiceId = userWithMetadata.practiceId || userWithMetadata.practice_id;
        }
        if (userWithMetadata.tenantId || userWithMetadata.tenant_id) {
          attributes.tenantId = userWithMetadata.tenantId || userWithMetadata.tenant_id;
        }

        // Session metadata (no tokens)
        if (userWithMetadata.sid) {
          attributes.sessionId = userWithMetadata.sid;
        }

        awsRum.addSessionAttributes(attributes);

        // Mirror tenant/practice into the keystroke-lag tracker so every
        // `note.keystroke_lag` event we emit is queryable per-org without
        // needing a join against `auth.user_session_started` events.
        updateKeystrokeLagBaseAttrs({
          practiceId:
            (attributes.practiceId as string | undefined) ?? undefined,
          tenantId: (attributes.tenantId as string | undefined) ?? undefined,
        });

        console.debug("✅ RUM: Added user/session attributes", {
          userId: user.sub,
          saxId: userWithMetadata.saxId || userWithMetadata.sax_id,
          practiceId: userWithMetadata.practiceId || userWithMetadata.practice_id,
        });
      } catch (error) {
        console.error("Failed to add user attributes to RUM:", error);
      }
    }
  }, [user, isLoading]);

  return <>{children}</>;
}

